import { useEffect, useRef, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link, useNavigate } from "react-router-dom";
import { PartyPopper, Plus, Sparkles, Clock, CircleCheck, CircleDollarSign, Award, ArrowRight, Trash2, Archive, Copy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FestivalBasketButton } from "@/components/FestivalBasketButton";

const STATE_LABELS = {
  draft: { label: "Draft", tone: "text-[#A3A3A3] border-[#525252]" },
  demo_active: { label: "Demo Active", tone: "text-[#CCFF00] border-[#CCFF00]" },
  demo_expired: { label: "Demo Expired", tone: "text-[#FFB020] border-[#FFB020]" },
  payment_pending: { label: "Payment Pending", tone: "text-[#FFB020] border-[#FFB020]" },
  active_paid: { label: "Active", tone: "text-[#CCFF00] border-[#CCFF00]" },
  override: { label: "Override", tone: "text-[#66C2FF] border-[#66C2FF]" },
  complimentary: { label: "Complimentary", tone: "text-[#66C2FF] border-[#66C2FF]" },
  expired: { label: "Expired", tone: "text-[#525252] border-[#525252]" },
  draft_expired: { label: "Draft Expired", tone: "text-[#FF4500] border-[#FF4500]" },
};

// Iter464 — Tier label beneath the status badge. Derived from the
// license `pass_type` (e.g. `starter_1day` → "Starter"). Shows for
// every role; omitted entirely when no pass has been purchased yet
// (genuine drafts / draft-expired festivals).
const TIER_LABELS = { starter: "Starter", standard: "Standard", pro: "Pro", unlimited: "Unlimited" };
const tierFromPassType = (pt) => {
  if (!pt) return null;
  const key = String(pt).split("_")[0];
  return TIER_LABELS[key] || null;
};

// Iter316 — Pre-activation states that can be deleted from the list.
// Mirrors the backend allow-list in `routes_festival.py::delete_festival`.
const DELETABLE_STATES = new Set(["draft", "demo_active", "demo_expired", "draft_expired"]);

export default function FestivalHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const ctl = useListControls(items, { name: (f) => f.name, fields: (f) => [f.name, f.venue, f.location] });
  // Iter323 — Archived festivals (auto-archived 30 days after end_at
  // by the scheduler). Loaded from a separate endpoint so a coach's
  // active list stays lean, and rendered as a distinct read-only
  // panel below the main grid — no trash icon, no state chip, muted
  // border. Tapping still opens the read-only detail page.
  const [archived, setArchived] = useState(null);
  const [showArchived, setShowArchived] = useState(true);
  // Iter316 — Undo-delete plumbing.
  const pendingRef = useRef(new Map());
  const hiddenRef = useRef(new Map());

  useEffect(() => {
    (async () => {
      try {
        const [mine, arch] = await Promise.all([
          api.get("/festival/mine"),
          // Iter323 — Endpoint is owner-scoped; a 404 just means no
          // archived festivals yet. Never blocks the main list.
          api.get("/festival/mine/archived").catch(() => ({ data: { items: [] } })),
        ]);
        setItems(mine.data.items || []);
        setArchived(arch.data?.items || []);
      } catch (e) {
        toast.error("Failed to load festivals");
        setItems([]);
        setArchived([]);
      }
    })();
  }, []);

  const isPlatformOwner = user?.role === "admin" || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");

  const undoDelete = (fid) => {
    const timer = pendingRef.current.get(fid);
    if (timer) clearTimeout(timer);
    pendingRef.current.delete(fid);
    const row = hiddenRef.current.get(fid);
    hiddenRef.current.delete(fid);
    if (row) {
      setItems((prev) => {
        if (!prev) return prev;
        // Re-insert in the original order (created_at desc means newest
        // first — just put it back at its saved index or append if the
        // list has since shrunk).
        const idx = Math.min(row.index, prev.length);
        const copy = [...prev];
        copy.splice(idx, 0, row.item);
        return copy;
      });
    }
    toast.success("Delete cancelled");
  };

  const scheduleDelete = (fest, e) => {
    e.preventDefault();
    e.stopPropagation();
    // If somehow re-triggered while a delete is already pending, no-op.
    if (pendingRef.current.has(fest.id)) return;
    // Snapshot the row + its index for Undo restoration.
    setItems((prev) => {
      const idx = (prev || []).findIndex((f) => f.id === fest.id);
      hiddenRef.current.set(fest.id, { item: fest, index: idx });
      return (prev || []).filter((f) => f.id !== fest.id);
    });
    const timer = setTimeout(async () => {
      pendingRef.current.delete(fest.id);
      hiddenRef.current.delete(fest.id);
      try {
        await api.delete(`/festival/${fest.id}`);
        toast.success("Draft festival deleted");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Delete failed");
        // Refetch so a failed delete doesn't leave the row stubbornly hidden.
        try {
          const { data } = await api.get("/festival/mine");
          setItems(data.items || []);
        } catch {}
      }
    }, 5000);
    pendingRef.current.set(fest.id, timer);
    toast(`Deleting "${fest.name}"…`, {
      description: "Undo within 5 seconds",
      duration: 5000,
      action: { label: "Undo", onClick: () => undoDelete(fest.id) },
    });
  };

  // Clean up any pending timers on unmount — otherwise a fast route
  // change would still fire the DELETE after leaving the page.
  useEffect(() => () => {
    for (const t of pendingRef.current.values()) clearTimeout(t);
    pendingRef.current.clear();
    hiddenRef.current.clear();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="festival-home">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-mono text-[#CCFF00] mb-1">RALLY-LAB · FESTIVAL LIVE OPS</div>
          <h1 className="h-display text-3xl sm:text-4xl flex items-center gap-2">
            <PartyPopper className="w-7 h-7 text-[#CCFF00]" />
            Your festivals
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Run pickleball festivals with live court management, public displays and event-pass activation.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <FestivalBasketButton />
          {isPlatformOwner && (
            <Link
              to="/festival/pricing"
              data-testid="festival-pricing-link"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black"
            >
              <CircleDollarSign className="w-4 h-4" /> Pricing
            </Link>
          )}
          <button
            type="button"
            onClick={() => navigate("/festival/new")}
            data-testid="festival-create-cta"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold"
            style={{ background: "#CCFF00", color: "#0A0A0A" }}
          >
            <Plus className="w-4 h-4" /> New festival
          </button>
        </div>
      </div>

      {items === null && (
        <div className="text-sm text-[#A3A3A3]" data-testid="festival-loading">Loading…</div>
      )}
      {items?.length === 0 && (
        <div className="card-surface p-10 text-center" data-testid="festival-empty">
          <Sparkles className="w-10 h-10 text-[#CCFF00] mx-auto mb-3" />
          <div className="h-display text-xl">No festivals yet</div>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Spin up your first festival to try live court management and a 5-minute demo.
          </p>
          <button
            type="button"
            onClick={() => navigate("/festival/new")}
            data-testid="festival-empty-cta"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold"
            style={{ background: "#CCFF00", color: "#0A0A0A" }}
          >
            <Plus className="w-4 h-4" /> Create festival
          </button>
        </div>
      )}

      {items && items.length > 0 && <ListControlsBar ctl={ctl} placeholder="Search festivals…" testid="festivals" />}
      {items && items.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="festival-list">
          {ctl.list.map((f) => {
            const st = STATE_LABELS[f.license?.state] || STATE_LABELS.draft;
            const canDelete = DELETABLE_STATES.has(f.license?.state);
            const tierLabel = tierFromPassType(f.license?.pass_type);
            return (
              <div
                key={f.id}
                data-testid={`festival-card-${f.id}`}
                className={`card-surface p-5 transition-colors relative group ${f.is_clone ? "hover:border-[#FFB020]" : "hover:border-[#CCFF00]"}`}
              >
                {/* Iter391 · Fix 1 — Amber "Backup Copy" banner on failsafe clones.
                     Distinguishes standby clones from the organiser's primary festival
                     so they can't be mistaken for a duplicate they meant to delete. */}
                {f.is_clone && (
                  <div
                    className="-mt-5 -mx-5 mb-3 bg-[#FFB020] text-black text-[10px] uppercase tracking-widest font-bold py-1.5 px-3 flex items-center gap-1.5"
                    data-testid={`festival-backup-banner-${f.id}`}
                  >
                    <Copy className="w-3 h-3" /> Backup Copy
                  </div>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={(e) => scheduleDelete(f, e)}
                    data-testid={`festival-delete-${f.id}`}
                    title="Delete draft"
                    className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center border border-[#262626] text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors z-10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <Link
                  to={`/festival/${f.id}`}
                  className="block"
                >
                  <div className="flex items-start justify-between gap-3 mb-2 pr-9">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-[#525252]">
                        {f.template === "friendly_mix" ? "Friendly Mix" : "Match / Scored"}
                      </div>
                      <div className="font-semibold text-lg truncate">{f.name}</div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${st.tone}`}>
                        {st.label}
                      </span>
                      {tierLabel && (
                        <span
                          data-testid={`festival-tier-${f.id}`}
                          className="text-[9px] uppercase tracking-widest text-[#A3A3A3]"
                        >
                          {tierLabel}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-[#A3A3A3] space-y-0.5">
                    <div className="flex items-center gap-2"><Clock className="w-3 h-3" /> {formatDateRange(f.start_at, f.end_at)}</div>
                    {f.venue_name && <div className="truncate">📍 {f.venue_name}</div>}
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-wider text-[#CCFF00] flex items-center gap-1">
                    Open <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* Iter323 — Archived section. Auto-archived 30 days after
          end_at by the scheduler; displayed here as a collapsible
          panel so completed festivals get their own read-only home
          instead of hiding with draft-expired ones on the main
          list. */}
      {archived && archived.length > 0 && (
        <div className="mt-10 pt-6 border-t border-[#262626]" data-testid="festival-archived-section">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            data-testid="festival-archived-toggle"
            className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white mb-4"
          >
            <Archive className="w-3.5 h-3.5" />
            Archived · {archived.length}
            <ArrowRight
              className={`w-3 h-3 transition-transform ${showArchived ? "rotate-90" : ""}`}
            />
          </button>
          {showArchived && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="festival-archived-list">
              {archived.map((f) => (
                <Link
                  key={f.id}
                  to={`/festival/${f.id}`}
                  data-testid={`festival-archived-card-${f.id}`}
                  className="card-surface p-5 hover:border-[#525252] border-[#141414] opacity-70 hover:opacity-100 transition-all block"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-[#525252]">
                        {f.template === "friendly_mix" ? "Friendly Mix" : "Match / Scored"}
                      </div>
                      <div className="font-semibold text-lg truncate text-[#A3A3A3]">{f.name}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-[#525252] border border-[#262626] px-1.5 py-0.5">
                      Complete
                    </span>
                  </div>
                  <div className="text-xs text-[#525252] space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3" /> {formatDateRange(f.start_at, f.end_at)}
                    </div>
                    {f.archived_at && (
                      <div className="text-[10px] text-[#525252]">
                        Archived {formatDateRange(f.archived_at, null)}
                      </div>
                    )}
                    {f.venue_name && <div className="truncate">📍 {f.venue_name}</div>}
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-[#525252] flex items-center gap-1">
                    Read-only <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatDateRange(start, end) {
  if (!start && !end) return "";
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const fmt = (d) => d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    if (!e) return fmt(s);
    if (s.toDateString() === e.toDateString()) return fmt(s);
    return `${fmt(s)} → ${fmt(e)}`;
  } catch {
    return start || "";
  }
}
